import { useEffect, useRef, useState } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { Headphones, ExternalLink, ChevronDown, ChevronUp, Play } from 'lucide-react';
import { usePlayer } from '../contexts/PlayerContext';

const RELEASES = [
  { 
    code: 'VVS001', 
    title: 'FromZero I',
    artist: 'fromnow',
    year: '2025',
    url: 'https://soundcloud.com/fromnowproductions/sets/fromzero-i',
    status: 'out'
  },
  { 
    code: 'VVS002', 
    title: 'FromZero II',
    artist: 'fromnow',
    year: '2025',
    url: 'https://soundcloud.com/fromnowproductions/sets/vvs002-fromzero-ii',
    status: 'out'
  },
  { 
    code: 'VVS003', 
    title: 'FromZero III',
    artist: 'fromnow',
    year: '2025',
    url: 'https://soundcloud.com/fromnowproductions/sets/fromzero-iii',
    status: 'out'
  },
  { 
    code: 'VVSX001', 
    title: 'TBA',
    artist: '//',
    year: '2025',
    url: null,
    status: 'incoming'
  },
];

function ReleaseCard({ release, isExpanded, onToggle, index, isMobile }) {
  const [coverUrl, setCoverUrl] = useState(null);
  const expandRef = useRef(null);
  const { playTrack } = usePlayer();

  // Fetch cover from SoundCloud oEmbed
  useEffect(() => {
    if (release.url && !coverUrl) {
      fetch(`https://soundcloud.com/oembed?url=${encodeURIComponent(release.url)}&format=json`)
        .then(res => res.json())
        .then(data => {
          if (data.thumbnail_url) {
            setCoverUrl(data.thumbnail_url);
          }
        })
        .catch(() => {
          // Silently fail - cover will just not show
        });
    }
  }, [release.url, coverUrl]);

  // Animate expand/collapse
  useEffect(() => {
    if (!expandRef.current) return;
    
    if (isExpanded) {
      expandRef.current.style.display = 'block';
      anime({
        targets: expandRef.current,
        height: [0, expandRef.current.scrollHeight],
        opacity: [0, 1],
        duration: 280,
        easing: 'easeOutCubic',
      });
    } else {
      anime({
        targets: expandRef.current,
        height: [expandRef.current.scrollHeight, 0],
        opacity: [1, 0],
        duration: 200,
        easing: 'easeInCubic',
        complete: () => {
          if (expandRef.current) {
            expandRef.current.style.display = 'none';
          }
        },
      });
    }
  }, [isExpanded]);

  const handlePlayGlobal = (e) => {
    e.stopPropagation();
    playTrack({
      url: release.url,
      title: release.title,
      artist: release.artist,
      coverUrl: coverUrl,
    });
  };

  if (release.status === 'incoming') {
    return (
      <div 
        className="release-item"
        data-testid={`release-${release.code.toLowerCase()}`}
        style={{
          padding: '28px 24px',
          borderBottom: '1px solid rgba(240,244,248,0.06)',
          opacity: 0,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <span style={{ 
              fontFamily: 'Space Grotesk', fontSize: '12px', letterSpacing: '0.12em',
              color: 'rgba(212,175,55,0.7)', width: '80px',
            }}>
              {release.code}
            </span>
            
            <span style={{
              fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(18px, 2.5vw, 24px)',
              textTransform: 'uppercase', color: 'var(--vvs-muted)',
            }}>
              {release.title}
            </span>
          </div>

          <span style={{
            fontFamily: 'Space Grotesk', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase',
            padding: '5px 12px', border: '1px solid rgba(212,175,55,0.2)', color: 'rgba(212,175,55,0.7)',
          }}>
            Incoming
          </span>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="release-item"
      data-testid={`release-${release.code.toLowerCase()}`}
      style={{
        borderBottom: '1px solid rgba(240,244,248,0.06)',
        opacity: 0,
        transition: 'background 0.3s',
      }}
    >
      {/* Main row - clickable to expand */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center',
          padding: isMobile ? '20px 16px' : '28px 24px',
          cursor: 'pointer',
          gap: isMobile ? '16px' : '0',
        }}
        onMouseEnter={e => { if (!isMobile) e.currentTarget.style.background = 'rgba(0,240,255,0.03)'; }}
        onMouseLeave={e => { if (!isMobile) e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '16px' : '24px' }}>
          {/* Cover thumbnail */}
          {coverUrl && (
            <img 
              src={coverUrl} 
              alt={release.title}
              style={{
                width: isMobile ? '48px' : '56px', 
                height: isMobile ? '48px' : '56px',
                objectFit: 'cover', borderRadius: '4px',
                flexShrink: 0,
              }}
            />
          )}
          
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '16px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <span style={{ 
                fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.12em',
                color: 'var(--vvs-cyan)',
              }}>
                {release.code}
              </span>
              <span style={{ 
                fontFamily: 'Space Grotesk', fontSize: '11px',
                color: 'var(--vvs-muted)',
              }}>
                {release.year}
              </span>
            </div>
            
            <span style={{
              fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(18px, 2.5vw, 24px)',
              textTransform: 'uppercase', color: '#F0F4F8',
            }}>
              {release.title}
            </span>
            
            <p style={{
              fontFamily: 'Space Grotesk', fontSize: '12px',
              color: 'var(--vvs-muted)', margin: '4px 0 0',
            }}>
              {release.artist}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Play in persistent player */}
          <button
            onClick={handlePlayGlobal}
            style={{
              background: 'rgba(0,240,255,0.1)', border: '1px solid rgba(0,240,255,0.3)',
              borderRadius: '50%', width: '36px', height: '36px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(0,240,255,0.2)';
              e.currentTarget.style.borderColor = 'rgba(0,240,255,0.5)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(0,240,255,0.1)';
              e.currentTarget.style.borderColor = 'rgba(0,240,255,0.3)';
            }}
            data-testid={`play-${release.code.toLowerCase()}`}
          >
            <Play size={14} style={{ color: 'var(--vvs-cyan)', marginLeft: '2px' }} fill="var(--vvs-cyan)" />
          </button>
          
          {/* External link */}
          <a
            href={release.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{
              fontFamily: 'Space Grotesk', fontSize: '11px', 
              letterSpacing: '0.12em', textTransform: 'uppercase',
              color: 'var(--vvs-cyan)', textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: '6px',
            }}
          >
            <Headphones size={14} /> Listen <ExternalLink size={10} />
          </a>
          
          {/* Expand toggle */}
          {isExpanded ? (
            <ChevronUp size={18} style={{ color: 'var(--vvs-muted)' }} />
          ) : (
            <ChevronDown size={18} style={{ color: 'var(--vvs-muted)' }} />
          )}
        </div>
      </div>

      {/* Expanded iframe */}
      <div 
        ref={expandRef}
        style={{
          display: 'none',
          overflow: 'hidden',
          padding: isExpanded ? '0 24px 24px' : '0 24px',
        }}
      >
        <iframe
          title={`${release.title} SoundCloud`}
          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(release.url)}&color=%23003478&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false`}
          width="100%"
          height="166"
          frameBorder="0"
          allow="autoplay"
          style={{ borderRadius: '4px' }}
        />
      </div>
    </div>
  );
}

export default function ReleasesPage() {
  const heroRef = useRef(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    document.title = 'Releases // VVS';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroRef.current) return;

    anime.timeline({ easing: 'easeOutExpo' })
      .add({ targets: '.rel-caption', translateY: [20, 0], opacity: [0, 1], duration: 700 })
      .add({ targets: '.rel-title', translateY: [60, 0], opacity: [0, 1], duration: 1000 }, '-=400')
      .add({ targets: '.rel-sub', translateY: [20, 0], opacity: [0, 1], duration: 600 }, '-=600');

    setTimeout(() => {
      anime({
        targets: '.release-item',
        translateY: [20, 0],
        opacity: [0, 1],
        duration: 600,
        delay: anime.stagger(100),
        easing: 'easeOutExpo',
      });
    }, 600);
  }, []);

  const handleToggle = (index) => {
    // Accordion - only one open at a time
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '64px', overflowX: 'hidden' }}>

      {/* Hero */}
      <section ref={heroRef} style={{
        minHeight: isMobile ? 'auto' : '40vh', 
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', 
        padding: isMobile ? '60px 20px 40px' : '80px 48px 40px', 
        position: 'relative',
      }} data-testid="releases-hero">
        <div style={{ position: 'absolute', top: 0, left: isMobile ? '20px' : '48px', right: isMobile ? '20px' : '48px', height: '1px', background: 'rgba(240,244,248,0.06)' }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
          <p className="rel-caption caption" style={{ marginBottom: '20px', opacity: 0 }}>
            Catalog
          </p>

          <h1 className="rel-title neon-heading" style={{
            fontSize: isMobile ? '48px' : 'clamp(48px, 10vw, 100px)',
            lineHeight: 0.9, letterSpacing: '-0.02em', textTransform: 'uppercase',
            margin: '0 0 20px', opacity: 0,
          }}>
            Releases
          </h1>

          <p className="rel-sub" style={{
            fontFamily: 'Space Grotesk', fontSize: isMobile ? '14px' : 'clamp(14px, 2vw, 18px)',
            color: 'var(--vvs-muted)', opacity: 0,
          }}>
            Official VVS catalog
          </p>
        </div>
      </section>

      {/* Releases list */}
      <section style={{ padding: isMobile ? '20px 20px 120px' : '40px 48px 100px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {RELEASES.map((release, i) => (
            <ReleaseCard
              key={release.code}
              release={release}
              isExpanded={expandedIndex === i}
              onToggle={() => handleToggle(i)}
              index={i}
              isMobile={isMobile}
            />
          ))}
        </div>
      </section>

    </div>
  );
}
